import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, ShieldCheck, Wallet, Wrench } from "lucide-react";

const PERM_INFO = {
  dono: { label: "Dono (acesso total)", cor: "bg-[#142766] text-white", Icon: ShieldCheck },
  operacional: { label: "Operacional", cor: "bg-[#DBEAFE] text-[#142766]", Icon: Wrench },
  financeiro: { label: "Financeiro", cor: "bg-[#F0FDF4] text-[#166534]", Icon: Wallet },
};

export default function MinhaEquipe() {
  const [lista, setLista] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", permissao: "operacional" });
  const [salvando, setSalvando] = useState(false);

  const load = () => api.get("/me/equipe").then(r => setLista(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const criar = async () => {
    setSalvando(true);
    try {
      await api.post("/me/equipe", form);
      toast.success("Subconta criada! Ela já pode logar na tela principal do MontaJá.");
      setOpen(false);
      setForm({ nome: "", email: "", senha: "", permissao: "operacional" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSalvando(false);
  };

  const toggleAtivo = async (u) => {
    try {
      await api.patch(`/me/equipe/${u.id}`, { ativo: !(u.ativo !== false) });
      toast.success(u.ativo !== false ? "Acesso desativado imediatamente" : "Acesso reativado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ativos = (lista || []).filter(u => u.ativo !== false).length;

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="label-cap">Conta</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1 flex items-center gap-2">
            <Users size={26} /> Minha Equipe
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Até 4 acessos ativos: 1 Dono + 2 Operacional + 1 Financeiro. Cada ação fica registrada com o nome de quem fez.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-sharp brand-bg text-white" data-testid="btn-nova-subconta"
              disabled={ativos >= 4}>+ Nova subconta</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nova subconta</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="label-cap">Nome</Label>
                <Input className="mt-1.5" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} data-testid="eq-nome" /></div>
              <div><Label className="label-cap">E-mail (login)</Label>
                <Input className="mt-1.5" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="eq-email" /></div>
              <div><Label className="label-cap">Senha</Label>
                <Input className="mt-1.5" type="text" value={form.senha} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} data-testid="eq-senha" /></div>
              <div><Label className="label-cap">Permissão</Label>
                <Select value={form.permissao} onValueChange={v => setForm(f => ({ ...f, permissao: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="eq-permissao"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operacional">Operacional — OS, chats, distribuição, manuais, pulmão (máx. 2)</SelectItem>
                    <SelectItem value="financeiro">Financeiro — apenas faturas, notas e relatórios (máx. 1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full btn-sharp brand-bg text-white" onClick={criar}
                disabled={salvando} data-testid="eq-salvar">Criar acesso</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-white divide-y divide-border" data-testid="equipe-lista">
        {lista === null && <div className="p-8 text-center text-muted-foreground">Carregando…</div>}
        {(lista || []).map(u => {
          const info = PERM_INFO[u.permissao] || PERM_INFO.dono;
          const inativo = u.ativo === false;
          return (
            <div key={u.id} className={`p-4 flex items-center gap-4 flex-wrap ${inativo ? "opacity-60" : ""}`}
              data-testid={`equipe-user-${u.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold">{u.name} {inativo && <span className="text-[10px] text-[#E60000] font-bold uppercase ml-1">desativado</span>}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${info.cor}`}>
                <info.Icon size={12} /> {info.label}
              </span>
              {u.subconta && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{inativo ? "Bloqueado" : "Ativo"}</span>
                  <Switch checked={!inativo} onCheckedChange={() => toggleAtivo(u)} data-testid={`equipe-toggle-${u.id}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        🔒 Ao desativar, o acesso é bloqueado instantaneamente. As subcontas usam a mesma tela de login do MontaJá.
      </p>
    </div>
  );
}
